/* Read-only payment-instructions display for the public offer page.

Shows only the rails the creditor populated, with copyable values. The case
owner sees this previewable on Case detail too. */
import { Card } from "./ui/card";
import { Bank, Envelope, DeviceMobile, Link as LinkIcon, NotePencil, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const RAILS = [
    { key: "mailing_address", label: "Mail a check to", icon: Envelope, multiline: true },
    { key: "zelle",           label: "Zelle",           icon: DeviceMobile },
    { key: "venmo",           label: "Venmo",           icon: DeviceMobile },
    { key: "cashapp",         label: "Cash App",        icon: DeviceMobile },
    { key: "ach_bank_name",   label: "ACH — bank name", icon: Bank },
    { key: "ach_routing",     label: "ACH — routing #", icon: Bank },
    { key: "ach_account",     label: "ACH — account #", icon: Bank },
    { key: "payment_link",    label: "Pay online",      icon: LinkIcon, isLink: true },
    { key: "notes",           label: "Notes",           icon: NotePencil, multiline: true },
];

function copy(value, label) {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => toast.success(`${label} copied`)).catch(() => {});
}

export default function PaymentInstructionsView({ instructions, amount }) {
    if (!instructions || Object.values(instructions).every((v) => !v)) {
        return (
            <Card className="p-5 bg-amber-50 border-amber-200" data-testid="payment-instructions-missing">
                <p className="text-sm text-amber-900">
                    The creditor hasn&apos;t posted payment instructions yet. Please contact them directly to arrange payment.
                </p>
            </Card>
        );
    }
    const rails = RAILS.filter((r) => (instructions[r.key] || "").toString().trim() !== "");
    return (
        <Card className="p-6 bg-emerald-50/60 border-emerald-200" data-testid="payment-instructions-view">
            <p className="overline">How to pay</p>
            <h3 className="font-serif text-2xl mt-1.5 text-[#0F1E3D]">
                Send <span className="font-mono">${(amount || 0).toLocaleString()}</span> via one of these methods
            </h3>
            <p className="text-sm text-slate-700 mt-2">
                LienLift Direct does not process this payment. Send the agreed amount directly to the creditor using any rail below. Save your confirmation receipt — the creditor will mark the case settled once received.
            </p>

            <div className="mt-5 space-y-2.5">
                {rails.map((r) => {
                    const v = instructions[r.key];
                    return (
                        <div key={r.key} className="flex items-start gap-3 bg-white border border-emerald-200 rounded-md p-3.5" data-testid={`pi-row-${r.key}`}>
                            {r.icon && <r.icon size={18} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />}
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">{r.label}</p>
                                {r.isLink ? (
                                    <a href={v} target="_blank" rel="noreferrer" className="text-sm text-[#2563EB] hover:underline break-all mt-1 inline-block">{v}</a>
                                ) : (
                                    <p className={`text-sm text-slate-900 mt-1 ${r.multiline ? "whitespace-pre-wrap" : "font-mono"}`}>{v}</p>
                                )}
                            </div>
                            {!r.isLink && !r.multiline && (
                                <button
                                    type="button"
                                    onClick={() => copy(v, r.label)}
                                    className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700 shrink-0"
                                    aria-label={`Copy ${r.label}`}
                                    data-testid={`pi-copy-${r.key}`}
                                >
                                    <Copy size={14} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
